import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { DEFAULT_PER_PAGE } from "@/helpers/constants";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import customWebhookService from "@/services/modules/customWebhook";
import { useCustomWebhookStore } from "@/stores/customWebhook";

import type { IClient, TemplateViewParams } from "@/models/clients";
import type { CustomWebhookTriggerPayload } from "@/models/customWebhook";

export const useCustomWebhooks = () => {
  const store = useCustomWebhookStore();
  const { getters } = useStore();
  const { showMessage } = useNotification();
  const { t } = useI18n();
  const { customWebhooks, loadingCustomWebhooks } = storeToRefs(store);

  const activeClient = computed<IClient | null>(
    () => getters["clients/active"]
  );
  const activeClientId = computed(() => activeClient.value?.id);

  const filters = ref<TemplateViewParams>({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    search: "",
    client_id: activeClientId.value,
  });

  const { fetchWrapper: getCustomWebhooks, loading: loadingWebhooks } =
    usePromiseWrapper(async (newFilters) => {
      if (loadingCustomWebhooks.value) {
        return;
      }
      const payload = { ...newFilters };
      if (payload.search === "") {
        delete payload.search;
      }
      if (activeClientId.value) {
        payload.client_id = activeClientId.value;
      }
      await store.getCustomWebhooks(payload);
    });

  const {
    fetchWrapper: triggerCustomWebhooks,
    loading: triggeringCustomWebhooks,
  } = usePromiseWrapper(
    async (appIds: string[], webhooks: CustomWebhookTriggerPayload[]) => {
      if (!appIds.length || !webhooks.length) {
        return;
      }
      await customWebhookService.triggerCustomWebhooks(appIds, webhooks);
      showMessage(t("CUSTOM_WEBHOOKS.TRIGGERED", webhooks.length), "success");
    }
  );

  watch(
    [activeClientId, filters],
    () => {
      getCustomWebhooks(filters.value);
    },
    { deep: true, immediate: true }
  );

  return {
    customWebhooks,
    getCustomWebhooks,
    loadingWebhooks,
    loadingCustomWebhooks,
    filters,
    triggerCustomWebhooks,
    triggeringCustomWebhooks,
  };
};
